import React from 'react';
import { NotebookPen } from 'lucide-react';
import SeverityFace from '../SeverityFace';
import { SEVERITY_SCALE } from '../../../../constants/journey';
import { formatFullDate, formatTime } from '../../../../utils/journeyDates';
import styles from './ReportEntries.module.css';

const formatRange = (entry) =>
	entry.end_time
		? `${formatTime(entry.start_time)} – ${formatTime(entry.end_time)}`
		: formatTime(entry.start_time);

// The report's flat cross-period listing, grouped back into days.
//
// Two renderings of the same grouping. On screen it is a list, which carries
// long journal text better at any width. On paper it is one table per day, with
// aligned columns a clinician can scan down — a printout is read across records
// rather than one entry at a time. Neither is ever visible in the other medium.
const ReportEntries = ({ entries = [] }) => {
	if (entries.length === 0) {
		return <p className={styles.empty}>No entries were recorded in this period.</p>;
	}

	const byDate = entries.reduce((groups, entry) => {
		(groups[entry.date] = groups[entry.date] || []).push(entry);
		return groups;
	}, {});

	const dates = Object.keys(byDate).sort();

	return (
		<>
			<div className={styles.report}>
				{dates.map((date) => (
					<section key={date} className={styles.day}>
						<h4 className={styles.dayTitle}>{formatFullDate(date)}</h4>
						<ul className={styles.entries}>
							{byDate[date].map((entry) => (
								<li key={entry.rating_log_id} className={styles.entry}>
									<SeverityFace rating={entry.rating} size={26} />
									<div className={styles.entryBody}>
										<div className={styles.entryHead}>
											<span className={styles.termName}>{entry.term_name}</span>
											<span className={styles.meta}>
												{formatRange(entry)}
												{' · '}
												{SEVERITY_SCALE[entry.rating]?.label}
											</span>
										</div>
										{entry.journal?.trim() && (
											<p className={styles.journal}>
												<NotebookPen className={styles.journalIcon} />
												{entry.journal.trim()}
											</p>
										)}
									</div>
								</li>
							))}
						</ul>
					</section>
				))}
			</div>

			<div className={styles.printReport}>
				{dates.map((date) => (
					<table key={date} className={styles.printTable}>
						<caption className={styles.printCaption}>
							{formatFullDate(date)}
						</caption>
						<thead>
							<tr>
								<th scope='col' className={styles.timeColumn}>
									Time
								</th>
								<th scope='col' className={styles.goalColumn}>
									Goal
								</th>
								<th scope='col'>Diary entry</th>
							</tr>
						</thead>
						<tbody>
							{byDate[date].map((entry) => (
								<tr key={entry.rating_log_id}>
									<td>{formatRange(entry)}</td>
									<td>
										<span className={styles.goalCell}>
											<SeverityFace rating={entry.rating} size={16} />
											{entry.term_name}
										</span>
									</td>
									<td>{entry.journal?.trim() || ''}</td>
								</tr>
							))}
						</tbody>
					</table>
				))}
			</div>
		</>
	);
};

export default ReportEntries;
